import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Copy, GraduationCap, Info, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { 
  getEpinPlans, 
  buyEpin 
} from "../../api/apiServices";

export default function EpinPayment() {
  const navigate = useNavigate();

  // --- UI States ---
  const [step, setStep] = useState(1); // 1: Select, 2: Confirm & PIN, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  // --- API Data ---
  const [examPlans, setExamPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // --- Form Inputs ---
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [phone, setPhone] = useState("");
  const [transactionPin, setTransactionPin] = useState("");

  // --- Final Success Data ---
  const [purchasedPins, setPurchasedPins] = useState([]);

  // Fetch Exam EPins configurations on mount
  useEffect(() => {
    const fetchExamPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await getEpinPlans();
        
        // FIXED: Extract target data from response.products based on your JSON structure
        if (response && response.products && Array.isArray(response.products)) {
          setExamPlans(response.products);
        } else if (Array.isArray(response)) {
          setExamPlans(response);
        } else {
          setError("Failed to fetch available exam pins.");
        }
      } catch (err) {
        console.error("E-Pin plan fetch error", err);
        setError(err?.response?.data?.message || "Error fetching exam card plans.");
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchExamPlans();
  }, []);

  const handleProceedToPay = (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      setError("Please pick an Exam E-Pin.");
      return;
    }
    if (!phone || phone.length < 11) {
      setError("Please input a valid phone number for PIN SMS delivery.");
      return;
    }
    setError("");
    setStep(2);
  };

  // FIXED: Access pricing dynamically using the `amount` property from payload maps
  const totalAmount = selectedPlan ? selectedPlan.amount * quantity : 0;

  const handleFinalPayment = async (e) => {
    e.preventDefault();
    if (transactionPin.length < 4) {
      setError("Enter your transaction PIN.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // FIXED: Payload mappings matching backend model updates cleanly
      const payload = {
        cardTypeId: selectedPlan.cardTypeId, // Maps to product structural identifiers
        quantity: Number(quantity),
        amount: Number(totalAmount),   
        phone: phone,                  
        pin: transactionPin
      };

      const res = await buyEpin(payload);

      if (res && res.cards && Array.isArray(res.cards)) {
        setPurchasedPins(res.cards);
      } else {
        const fallbackToken = res.token || res.pin || "PIN-PENDING";
        setPurchasedPins(Array.from({ length: quantity }, (_, i) => ({
          serial: res.serial || "SN-PENDING",
          pin: fallbackToken,
          pinCode: fallbackToken
        })));
      }
      setStep(3);
    } catch (err) {
      console.error("E-Pin processing error", err);
      setError(err?.response?.data?.message || "Failed to execute transaction. Please review your balance.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <header className="bg-white border-b px-4 py-3.5 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <button
          onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))}
          className="p-1 hover:bg-gray-100 rounded-full transition"
        >
          <ArrowLeft size={22} className="text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-800">Exam E-Pin Purchase</h1>
      </header>

      <main className="px-4 pt-6 max-w-md mx-auto">
        {error && (
          <div className="mb-4 bg-red-50 text-red-600 border border-red-200 text-sm rounded-xl p-4 font-medium flex items-center gap-2">
            <Info size={18} />
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="space-y-6"
            >
              <form onSubmit={handleProceedToPay} className="bg-white rounded-2xl border p-5 shadow-sm space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Select Examination Type
                  </label>
                  {loadingPlans ? (
                    <div className="h-12 bg-gray-50 border rounded-xl animate-pulse flex items-center px-4 text-xs text-gray-400">Loading exam pins...</div>
                  ) : (
                    <select
                      onChange={(e) => {
                        // FIXED: Lookup mapped plans against target cardTypeId key configurations
                        const plan = examPlans.find(p => String(p.cardTypeId) === e.target.value);
                        setSelectedPlan(plan || null);
                      }}
                      value={selectedPlan?.cardTypeId || ""}
                      className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition"
                    >
                      <option value="">-- Choose Exam Checker --</option>
                      {examPlans.map((p) => (
                        <option key={p.cardTypeId} value={p.cardTypeId}>
                          {p.name} (₦{p.amount.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Quantity
                  </label>
                  <select
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition"
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Sms / Contact Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-sm text-gray-800 focus:outline-none focus:border-[#3866A3] transition"
                  />
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    The pins will be texted to this phone number on success.
                  </span>
                </div>

                <button
                  type="submit"
                  className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition shadow-md mt-4"
                >
                  Buy E-PIN (₦{totalAmount.toLocaleString()})
                </button>
              </form>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl border p-6 shadow-md space-y-6 text-center"
            >
              <div className="w-14 h-14 bg-[#E9EDF6] rounded-full flex items-center justify-center mx-auto text-[#3866A3]">
                <GraduationCap size={28} />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-800">Confirm Pin Purchase</h3>
                <p className="text-gray-500 text-sm mt-1">Please confirm details before finalizing</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-500">Exam Code</span>
                  <span className="font-semibold text-gray-800 uppercase">{selectedPlan?.cardTypeId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Service</span>
                  <span className="font-semibold text-gray-800">{selectedPlan?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Quantity</span>
                  <span className="font-semibold text-gray-800">{quantity} units</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">SMS Target</span>
                  <span className="font-semibold text-gray-800">{phone}</span>
                </div>
                <hr className="border-gray-200 my-1" />
                <div className="flex justify-between text-base font-bold">
                  <span className="text-gray-700">Total Bill</span>
                  <span className="text-[#3866A3]">₦{totalAmount.toLocaleString()}</span>
                </div>
              </div>

              <form onSubmit={handleFinalPayment} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Enter Transaction PIN
                  </label>
                  <input
                    type="password"
                    maxLength={4}
                    value={transactionPin}
                    onChange={(e) => setTransactionPin(e.target.value)}
                    placeholder="••••"
                    className="w-32 h-12 bg-gray-50 border border-gray-200 rounded-xl px-4 text-center text-xl font-bold tracking-widest focus:outline-none focus:border-[#3866A3] transition mx-auto block"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition disabled:opacity-75 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Processing...
                    </>
                  ) : (
                    `Pay ₦${totalAmount.toLocaleString()}`
                  )}
                </button>
              </form>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl border p-6 shadow-md text-center space-y-6"
            >
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto text-emerald-500">
                <CheckCircle2 size={40} />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-800">Pin Generated Successfully!</h3>
                <p className="text-gray-500 text-sm mt-1">
                  The exam checker codes are listed below and have been sent to {phone}.
                </p>
              </div>

              <div className="space-y-3">
                {purchasedPins.map((item, index) => {
                  const displayPin = item.pinCode || item.pin || "PIN-UNKNOWN";
                  return (
                    <div key={index} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-left relative overflow-hidden">
                      <span className="text-[10px] font-bold text-emerald-800 uppercase block tracking-wider mb-1">
                        Pin Voucher {index + 1}
                      </span>
                      <p className="text-base font-bold text-emerald-950 tracking-wider pr-14">
                        {displayPin}
                      </p>
                      {item.serial && (
                        <p className="text-xs text-emerald-600 mt-1 font-mono">
                          Serial: {item.serial}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => copyToClipboard(displayPin, index)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-emerald-100 border border-emerald-200 rounded-lg text-emerald-700 transition z-10"
                      >
                        {copiedIndex === index ? (
                          <span className="text-[10px] font-bold text-emerald-600">Copied</span>
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => navigate("/")}
                className="w-full h-12 bg-[#3866A3] text-white rounded-xl font-bold hover:bg-[#2e558a] transition"
              >
                Back to Dashboard
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}